import React from "react";
import { Users } from "lucide-react";

/* ===================================================== */
/* HELPERS                                               */
/* ===================================================== */

const TYPE_META = {
  internal: {
    label: "Internal",
    className: "bg-slate-100 text-slate-700",
  },
  contractor: {
    label: "Contractor",
    className: "bg-indigo-100 text-indigo-700",
  },
  founders: {
    label: "Founders",
    className: "bg-amber-100 text-amber-700",
  },
};

function getTypeMeta(type) {
  return TYPE_META[type] || null;
}

/* ===================================================== */
/* COMPONENT                                             */
/* ===================================================== */

export default function TeamCard({
  team,
  onEdit,
  onDelete,
  onOpen,
  className = "",
}) {
  const name = team?.name?.trim() || "Unnamed Team";

  const memberCount = Array.isArray(team?.members)
    ? team.members.length
    : 0;

  const typeMeta = getTypeMeta(team?.type);

  const isProtected =
    team?.isDefault === true || team?.type === "founders";

  const isContractor = team?.type === "contractor";

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div
      className={`border rounded-xl p-4 bg-white shadow-sm flex items-start justify-between hover:shadow-md transition ${className}`}
    >
      {/* ================= LEFT ================= */}
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-md bg-gray-50 flex items-center justify-center border flex-shrink-0">
          <Users className="w-5 h-5 text-gray-600" />
        </div>

        <div className="min-w-0">
          {/* Name */}
          <div className="font-medium truncate flex items-center gap-2">
            {name}

            {/* ⚠️ Missing name warning */}
            {name === "Unnamed Team" && (
              <span className="text-xs text-rose-500">
                (needs attention)
              </span>
            )}

            {/* 🏷 Type Badge */}
            {typeMeta && (
              <span
                className={`text-[10px] px-2 py-0.5 rounded ${typeMeta.className}`}
              >
                {typeMeta.label}
              </span>
            )}
          </div>

          {/* Meta */}
          <div className="text-xs text-gray-500 truncate mt-1">
            {memberCount}{" "}
            {memberCount === 1 ? "member" : "members"}
          </div>

          {/* 🧠 Contractor hint */}
          {isContractor && (
            <div className="text-[11px] text-indigo-600 mt-1">
              External company team
            </div>
          )}
        </div>
      </div>

      {/* ================= ACTIONS ================= */}
      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
        <button
          onClick={() => onOpen?.(team)}
          className="text-sm text-indigo-600 hover:underline"
        >
          Open
        </button>

        {/* ✏️ Edit rules */}
        <button
          onClick={() => onEdit?.(team)}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
        >
          Edit
        </button>

        {/* 🗑 Delete rules */}
        {!isProtected && (
          <button
            onClick={() => onDelete?.(team)}
            className="px-2 py-1 text-sm border rounded text-rose-600 hover:bg-rose-50"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}